import { Button, Card, CopyableValue, Stack, Text } from '@components/ui';
import { CancelJobDialog, JobKindNote } from '@features/jobs';
import { JobLocationMap } from '@features/jobs/job-detail';
import { apiErrorMessage } from '@lib/api/apiError';
import { useCancelJobByBuyerMutation } from '@lib/api/jobs';
import Box from '@mui/material/Box';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import type { JobData } from './currentJobDetailBuyerTypes';

const pageSx = {
  minHeight: '100vh',
  width: '100%',
  maxWidth: 1120,
  mx: 'auto',
  boxSizing: 'border-box',
  bgcolor: 'background.default',
  px: { xs: 2, md: 4 },
  py: 3,
};

const valueSx = {
  textAlign: { xs: 'left', sm: 'right' },
  fontSize: 14,
  fontWeight: 500,
};

const DetailRow = ({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: ReactNode;
  wide?: boolean;
}): JSX.Element => (
  <Box
    sx={{
      gridColumn: wide ? '1 / -1' : undefined,
      borderRadius: 1,
      bgcolor: 'action.hover',
      px: 1.5,
      py: 1,
    }}
  >
    <Text variant="overline" tone="muted">
      {label}
    </Text>
    <Box sx={{ mt: 0.25, fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Box>
  </Box>
);


const CurrentJobDetailBuyer = (): JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const cancelJob = useCancelJobByBuyerMutation();
  const navigate = useNavigate();
  const jobData = (location.state as { currentJobData?: JobData })
    ?.currentJobData;

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const handleCancelJobsConfirm = (reason: string): void => {
    cancelJob.mutate(
      { jobId: jobData?.id, reason },
      {
        onSuccess: (res) => {
          setCancelDialogOpen(false);
          toast.success(res.data?.message ?? 'Job cancelled');
          setTimeout(() => navigate('/'), 2000);
        },
        onError: (error) => {
          toast.error(
            apiErrorMessage(error) ?? 'Something went wrong'
          );
        },
      }
    );
  };

  const canCancelJob =
    jobData?.status === 'Posted' ||
    (jobData?.status === 'Accepted' &&
      !jobData?.ratingStartedAt &&
      !jobData?.onMyWay);

  const isNonAutomotive =
    jobData?.category?.isAutomotive === false ||
    jobData?.lineOfBusiness === 'ASSET_VERIFICATION' ||
    jobData?.category?.lineOfBusiness === 'ASSET_VERIFICATION';

  const headerTitle = isNonAutomotive
    ? jobData?.description ||
      jobData?.subCategory?.name ||
      jobData?.category?.name ||
      'Job Details'
    : jobData?.make || jobData?.description || 'Job Details';

  const categorySummary = [jobData?.category?.name, jobData?.subCategory?.name]
    .filter(Boolean)
    .join(' - ');

  if (!jobData) {
    return (
      <Box sx={pageSx}>
        <Text tone="muted">{t('common.loading')}</Text>
      </Box>
    );
  }

  const trimmedLink = jobData?.link?.trim();
  const linkUrl = trimmedLink
    ? /^https?:\/\//i.test(trimmedLink)
      ? trimmedLink
      : `https://${trimmedLink}`
    : null;

  const mapQuery =
    jobData?.latitude != null && jobData?.longitude != null
      ? `${jobData.latitude},${jobData.longitude}`
      : jobData?.address;

  const mapUrl = mapQuery
    ? `https://maps.google.com?q=${encodeURIComponent(mapQuery)}`
    : null;


  return (
    <Box sx={pageSx}>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          borderRadius: 3,
          border: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          px: 3,
          py: 2.5,
        }}
      >
        <Stack gap="xs" sx={{ flex: 1, minWidth: 0 }}>
          <Text as="h2" variant="title">
            {headerTitle}
          </Text>
          <Text tone="muted" sx={{ fontSize: 14 }}>
            {categorySummary}
          </Text>
          <JobKindNote job={jobData} />
        </Stack>
        <Box
          sx={{
            mx: 3,
            height: 40,
            width: '1px',
            bgcolor: 'divider',
            display: { xs: 'none', sm: 'block' },
          }}
        />
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, flexShrink: 0 }}>
          <Text sx={{ display: 'block', fontSize: 22, fontWeight: 700 }}>
            ${jobData.buyerCost}
          </Text>
          <Text variant="caption" tone="muted">
            {t('jobDetail.jobValue')}
          </Text>
        </Box>
        {canCancelJob ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={() => setCancelDialogOpen(true)}
          >
            {t('jobDetail.cancelJob')}
          </Button>
        ) : null}
      </Box>
      <CancelJobDialog
        open={cancelDialogOpen}
        loading={cancelJob.isPending}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelJobsConfirm}
      />

      <Card sx={{ mb: 2 }}>
        <Text as="h2" variant="subtitle" sx={{ mb: 1.5 }}>
          {t('jobDetail.info')}
        </Text>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          <DetailRow label={t('jobDetail.description')} wide>
            <CopyableValue value={jobData.description || 'N/A'} sx={valueSx} />
          </DetailRow>
          {!isNonAutomotive ? (
            <DetailRow label={t('jobDetail.year')}>
              <CopyableValue value={jobData.year || 'N/A'} sx={valueSx} />
            </DetailRow>
          ) : null}
          <DetailRow label={t('jobDetail.make')}>
            <CopyableValue value={jobData.make || 'N/A'} sx={valueSx} />
          </DetailRow>
          <DetailRow label={t('jobDetail.model')}>
            <CopyableValue value={jobData.model || 'N/A'} sx={valueSx} />
          </DetailRow>
          <DetailRow label={t('jobDetail.askingPrice')}>
            <CopyableValue value={jobData.askingPrice ?? 'N/A'} sx={valueSx} />
          </DetailRow>
          <DetailRow label={t('jobDetail.vin')}>
            <CopyableValue value={jobData.vinOrSerial || 'N/A'} sx={valueSx} />
          </DetailRow>
          {linkUrl ? (
            <DetailRow label={t('hire.linkToListing')}>
              <Box
                component="a"
                href={linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-block',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'primary.main',
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {linkUrl}
              </Box>
            </DetailRow>
          ) : null}
        </Box>
      </Card>

      <Card sx={{ mb: 2 }}>
        <Text as="h2" variant="subtitle" sx={{ mb: 1.5 }}>
          {t('jobDetail.location')}
        </Text>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          <DetailRow label={t('jobDetail.address')}>
            <CopyableValue value={jobData.address} sx={valueSx} />
          </DetailRow>
          <DetailRow label={t('jobDetail.pointOfContact')}>
            <CopyableValue value={jobData.contactName} sx={valueSx} />
          </DetailRow>
        </Box>
        {mapUrl ? (
          <Box
            sx={{
              mt: 2,
              overflow: 'hidden',
              borderRadius: '10px',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                px: 1.5,
                py: 1.25,
              }}
            >
              <Text
                variant="overline"
                tone="muted"
                sx={{ letterSpacing: '0.8px' }}
              >
                {t('jobDetail.mapPreview')}
              </Text>
              <Box
                component="a"
                href={mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {t('jobDetail.openInMaps')}
              </Box>
            </Box>
            <JobLocationMap
              latitude={jobData?.latitude}
              longitude={jobData?.longitude}
              address={jobData?.address}
            />
          </Box>
        ) : null}
      </Card>

      <Card sx={{ mb: 2 }}>
        <Text as="h2" variant="subtitle" sx={{ mb: 1.5 }}>
          {t('jobDetail.specialInstructions')}
        </Text>
        <Text tone="muted" sx={{ mt: 0, fontSize: 15, lineHeight: 1.5 }}>
          {jobData.specialInstructions ?? 'None'}
        </Text>
      </Card>
    </Box>
  );
};

export default CurrentJobDetailBuyer;
